/**
 * UniverseMap — The Corpus, made visible.
 *
 * Every comprehended system drawn as one galaxy around the platform core.
 * The geometry is INFORMATION, not decoration:
 *   - ANGLE   = industry vertical. Classified verticals own contiguous arcs
 *     sized by their share of the corpus, so the shape of the map IS the shape
 *     of the coverage. The unclassified long tail scatters across the full
 *     circle — honest: most of a real corpus is long tail.
 *   - RADIUS  = comprehension depth. Portfolio connectors orbit closest to the
 *     core, promoted next, indexed furthest — "the value compounds toward the
 *     centre" drawn literally.
 *   - SIZE    = classified-field volume (log-scaled).
 *
 * Data: the same public catalogue the directory uses (/providers.json) —
 * fetched at runtime on SaaS, or passed pre-bundled via `providers` for the
 * sovereign/air-gapped build (Rule 12: zero egress). Names only, never logos.
 *
 * Interactions: hover names a system; clicking the core opens the platform
 * reveal ("the platform that holds it together"); clicking a dot goes to the
 * corpus directory. One ambient motion — a slow orbital drift — disabled under
 * prefers-reduced-motion and when the tab is hidden.
 *
 * Theme-aware: all colours are read from the live CSS custom properties
 * (--chart-1…8, --brand-*, --foreground…) at draw time and re-read when the
 * `.dark` class flips, so the galaxy belongs to whichever theme it sits in.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../button';

export interface UniverseVendor {
  readonly key: string;
  readonly vendor: string;
  readonly fields?: number;
  readonly promoted?: boolean;
  readonly isPortfolioVendor?: boolean;
  readonly vertical?: string;
}

export interface UniverseMapProps {
  /** Pre-bundled catalogue (sovereign path — no network). Wins over fetchUrl. */
  readonly providers?: readonly UniverseVendor[];
  /** Catalogue JSON served from public/ (the SaaS marketing-site path). */
  readonly fetchUrl?: string;
  /** Where a clicked system leads (the corpus directory). */
  readonly directoryHref?: string;
}

// ── deterministic per-vendor jitter (stable across loads — no Math.random) ──────
function hash01(s: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}

interface Dot {
  x: number; y: number;       // unit-space position (relative, pre-rotation)
  r: number; alpha: number;
  color: number;              // palette index; -1 = long-tail neutral
  vendor: string; vertical: string;
  fields: number; promoted: boolean; portfolio: boolean;
}

interface Sector { name: string; start: number; end: number; color: number; count: number }

const TAU = Math.PI * 2;
/** Bands as fractions of the outer radius. */
const BAND = { coreEdge: 0.14, portfolio: [0.17, 0.24], promoted: [0.28, 0.60], indexed: [0.66, 0.97] } as const;

function buildGalaxy(vendors: readonly UniverseVendor[]): { dots: Dot[]; sectors: Sector[] } {
  const byVertical = new Map<string, UniverseVendor[]>();
  const longTail: UniverseVendor[] = [];
  for (const v of vendors) {
    if (v.vertical) {
      const arr = byVertical.get(v.vertical) ?? [];
      arr.push(v); byVertical.set(v.vertical, arr);
    } else longTail.push(v);
  }

  // Arc share by sqrt(count) — keeps the biggest vertical from swallowing the circle.
  const named = [...byVertical.entries()].sort((a, b) => b[1].length - a[1].length);
  const weights = named.map(([, list]) => Math.sqrt(list.length));
  const totalW = weights.reduce((s, w) => s + w, 0) || 1;
  const GUTTER = 0.012 * TAU;
  const usable = TAU - GUTTER * named.length;

  const sectors: Sector[] = [];
  const dots: Dot[] = [];
  let angle = -Math.PI / 2; // first sector starts at 12 o'clock

  const place = (v: UniverseVendor, a0: number, a1: number, color: number) => {
    const [lo, hi] = v.isPortfolioVendor ? BAND.portfolio : v.promoted ? BAND.promoted : BAND.indexed;
    const t = hash01(v.key, 1);
    const rad = lo + (hi - lo) * Math.sqrt(hash01(v.key, 2)); // sqrt → uniform areal density
    const th = a0 + (a1 - a0) * t;
    const fields = v.fields ?? 0;
    dots.push({
      x: Math.cos(th) * rad, y: Math.sin(th) * rad,
      r: (v.isPortfolioVendor ? 1.9 : 0.9) + Math.log10(fields + 1) / 6,
      alpha: v.isPortfolioVendor ? 0.95 : v.promoted ? 0.78 : 0.34,
      color,
      vendor: v.vendor, vertical: v.vertical ?? 'Long tail',
      fields, promoted: !!v.promoted, portfolio: !!v.isPortfolioVendor,
    });
  };

  named.forEach(([name, list], i) => {
    const span = (weights[i]! / totalW) * usable;
    const start = angle; const end = angle + span;
    sectors.push({ name, start, end, color: i % 8, count: list.length });
    for (const v of list) place(v, start, end, i % 8);
    angle = end + GUTTER;
  });
  // The long tail wraps the whole circle — the deep field behind the classified arcs.
  for (const v of longTail) place(v, -Math.PI / 2, -Math.PI / 2 + TAU, -1);

  return { dots, sectors };
}

/** Live theme colours, read from the CSS custom properties the site already owns. */
function readTokens(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const get = (n: string, fb: string) => cs.getPropertyValue(n).trim() || fb;
  return {
    charts: Array.from({ length: 8 }, (_, i) => get(`--chart-${i + 1}`, get('--brand-500', 'transparent'))),
    tail: get('--muted-foreground', get('--foreground', 'transparent')),
    core: get('--brand-600', 'transparent'),
    coreSoft: get('--brand-400', 'transparent'),
    ring: get('--border', 'transparent'),
  };
}

export function UniverseMap({ providers, fetchUrl = '/providers.json', directoryHref = '/corpus' }: UniverseMapProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [vendors, setVendors] = useState<readonly UniverseVendor[] | null>(providers ?? null);
  const [hover, setHover] = useState<{ x: number; y: number; dot: Dot } | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const rotationRef = useRef(0);
  const hoverRef = useRef<Dot | null>(null);

  useEffect(() => {
    if (providers) return;
    let alive = true;
    fetch(fetchUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { providers?: UniverseVendor[] }) => { if (alive && j.providers) setVendors(j.providers); })
      .catch(() => { /* the page's prose + stats remain the no-data story */ });
    return () => { alive = false; };
  }, [providers, fetchUrl]);

  const galaxy = useMemo(() => (vendors ? buildGalaxy(vendors) : null), [vendors]);

  // screen-space dot positions from the last draw — the hover hit-test target
  const screenDots = useRef<{ sx: number; sy: number; dot: Dot }[]>([]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current; const wrap = wrapRef.current;
    if (!canvas || !wrap || !galaxy) return;
    const dpr = window.devicePixelRatio || 1;
    const w = wrap.clientWidth; const h = wrap.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const tk = readTokens(canvas);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2; const cy = h / 2;
    const R = Math.min(w, h) / 2 - 8;
    const rot = rotationRef.current;
    const cosR = Math.cos(rot); const sinR = Math.sin(rot);

    // orbit guides — one faint ring per comprehension band
    ctx.strokeStyle = tk.ring; ctx.globalAlpha = 0.5; ctx.lineWidth = 1;
    for (const rr of [BAND.portfolio[1], BAND.promoted[1], BAND.indexed[1]]) {
      ctx.beginPath(); ctx.arc(cx, cy, rr * R, 0, TAU); ctx.stroke();
    }

    const hovered = hoverRef.current;
    const pts: { sx: number; sy: number; dot: Dot }[] = [];
    for (const d of galaxy.dots) {
      const x = d.x * cosR - d.y * sinR;
      const y = d.x * sinR + d.y * cosR;
      const sx = cx + x * R; const sy = cy + y * R;
      const isHover = hovered === d;
      ctx.globalAlpha = isHover ? 1 : d.alpha;
      ctx.fillStyle = d.color >= 0 ? tk.charts[d.color]! : tk.tail;
      ctx.beginPath();
      ctx.arc(sx, sy, isHover ? d.r + 2 : d.r, 0, TAU);
      ctx.fill();
      if (isHover) { ctx.globalAlpha = 0.35; ctx.beginPath(); ctx.arc(sx, sy, d.r + 7, 0, TAU); ctx.strokeStyle = ctx.fillStyle; ctx.stroke(); }
      pts.push({ sx, sy, dot: d });
    }
    screenDots.current = pts;

    // the core — the platform that holds it together
    const coreR = BAND.coreEdge * R;
    const glow = ctx.createRadialGradient(cx, cy, coreR * 0.2, cx, cy, coreR * 2.2);
    glow.addColorStop(0, tk.coreSoft); glow.addColorStop(1, 'transparent');
    ctx.globalAlpha = 0.28; ctx.fillStyle = glow;
    ctx.beginPath(); ctx.arc(cx, cy, coreR * 2.2, 0, TAU); ctx.fill();
    ctx.globalAlpha = 1; ctx.fillStyle = tk.core;
    ctx.beginPath(); ctx.arc(cx, cy, coreR, 0, TAU); ctx.fill();
  }, [galaxy]);

  // ambient drift — one slow orbit; still under reduced motion / hidden tab
  useEffect(() => {
    if (!galaxy) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { draw(); return; }
    let raf = 0; let last = performance.now();
    const tick = (now: number) => {
      if (!document.hidden) {
        rotationRef.current += ((now - last) / 1000) * (TAU / 420); // one revolution ≈ 7 min
        draw();
      }
      last = now;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [galaxy, draw]);

  // redraw on resize + theme flip
  useEffect(() => {
    if (!galaxy) return;
    const ro = new ResizeObserver(() => draw());
    if (wrapRef.current) ro.observe(wrapRef.current);
    const mo = new MutationObserver(() => draw());
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    draw();
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [galaxy, draw]);

  const onMove = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
    let best: { sx: number; sy: number; dot: Dot } | null = null; let bestD = 121; // 11px
    for (const p of screenDots.current) {
      const dx = p.sx - mx; const dy = p.sy - my; const d2 = dx * dx + dy * dy;
      if (d2 < bestD) { bestD = d2; best = p; }
    }
    hoverRef.current = best?.dot ?? null;
    setHover(best ? { x: best.sx, y: best.sy, dot: best.dot } : null);
  };

  const legend = useMemo(() => (galaxy ? [...galaxy.sectors].sort((a, b) => b.count - a.count).slice(0, 8) : []), [galaxy]);
  const total = vendors?.length ?? 0;

  return (
    <div className="not-prose">
      <div
        ref={wrapRef}
        className="relative w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)]"
        style={{ height: 'min(72vh, 620px)' }}
        onMouseMove={onMove}
        onMouseLeave={() => { hoverRef.current = null; setHover(null); }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`The Inferrex Universe: ${total || 'thousands of'} comprehended systems arranged around the platform core — angle groups them by industry, distance from the centre shows comprehension depth.`}
          className={hover ? 'cursor-pointer' : undefined}
          onClick={() => { if (hoverRef.current) window.location.assign(directoryHref); }}
        />

        {/* the centre is a real, focusable control — “click the centre to see the platform” */}
        <button
          type="button"
          aria-expanded={revealOpen}
          onClick={() => setRevealOpen((o) => !o)}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full text-xs font-semibold tracking-wide text-[color:var(--brand-50)] outline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand-400)]"
          style={{ width: 'min(17vw, 120px)', height: 'min(17vw, 120px)', background: 'transparent' }}
        >
          Inferrex
        </button>

        {hover && !revealOpen && (
          <div
            className="pointer-events-none absolute z-10 max-w-xs rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 shadow-lg"
            style={{ left: Math.min(hover.x + 14, (wrapRef.current?.clientWidth ?? 300) - 250), top: Math.max(hover.y - 12, 8) }}
          >
            <p className="text-sm font-semibold leading-tight text-[var(--foreground)]">{hover.dot.vendor}</p>
            <p className="mt-0.5 text-xs leading-tight text-[var(--muted-foreground)]">
              {hover.dot.vertical}
              {hover.dot.fields > 0 ? ` · ${hover.dot.fields.toLocaleString()} fields classified` : ''}
              {hover.dot.portfolio ? ' · portfolio' : hover.dot.promoted ? ' · ready' : ''}
            </p>
          </div>
        )}

        {revealOpen && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[var(--card)]/85 p-6 backdrop-blur-sm" onClick={() => setRevealOpen(false)}>
            <div className="max-w-md rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <p className="font-mono text-xs uppercase tracking-widest text-[var(--brand-600)]">/ the centre</p>
              <h3 className="mt-2 text-xl font-semibold text-[var(--foreground)]">The platform that holds it together.</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--muted-foreground)]">
                Every point on this map has been read, classified by meaning, and reconciled into one
                canonical language. That shared layer at the centre is why any system here can exchange
                data with any other — and why each new system makes the next one easier to place.
              </p>
              <div className="mt-5 flex gap-3">
                <Button size="sm" onClick={() => window.location.assign(directoryHref)}>Browse The Corpus</Button>
                <Button size="sm" variant="outline" onClick={() => window.location.assign('/claims')}>Audited scope</Button>
                <Button size="sm" variant="ghost" onClick={() => setRevealOpen(false)}>Close</Button>
              </div>
            </div>
          </div>
        )}

        {!galaxy && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-[var(--muted-foreground)]">Charting the corpus…</p>
          </div>
        )}
      </div>

      {galaxy && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {legend.map((s) => (
            <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
              <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ background: `var(--chart-${s.color + 1})` }} />
              {s.name} · {s.count.toLocaleString()}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
            <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-[var(--muted-foreground)] opacity-40" />
            Long tail · {(total - galaxy.sectors.reduce((s, l) => s + l.count, 0)).toLocaleString()}
          </span>
          <span className="ml-auto font-mono text-xs uppercase tracking-wider text-[var(--muted-foreground)]">
            {total.toLocaleString()} systems · closer to the core = deeper comprehension
          </span>
        </div>
      )}
    </div>
  );
}
